// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AccessDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import { INSERT, UPDATE, DELETE } from 'logic/data/Izenda.ObjectStateConstants';
import { ASSIGN_TYPE } from 'logic/data/Izenda.AccessConstants';
import { uniqueId, isEmpty, forEach, isNil, includes } from 'lodash/fp';

@tracker
export default class AccessDetail {
  @observable accessors: Array<any> = [];
  @observable accessRight = null;
  @observable assignedType = null;
  @observable id = null;
  @observable permissionId = null;
  @observable reportId = null;
  @observable selected = false;
  @observable state = INSERT;
  @observable accessRightId = null;
  @observable reportAccessRightId = null;
  @observable dashboardAccessRightId = null;
  @observable assignedTypeName = null;
  @observable accessorNames = [];
  @observable reportAccessRights = null;
  @observable dashboardAccessRights = null;

  @computed
  get hasRolesAccessor() {
    return this.assignedType === ASSIGN_TYPE.ROLE;
  }

  @computed
  get hasUserAccessor() {
    return this.assignedType === ASSIGN_TYPE.USER;
  }

  @computed
  get accessorsInfo() {
    return this.accessors.map((item, index) => ({
      id: item,
      name: this.accessorNames[index]
    }));
  }

  constructor(data) {
    this.localRoles = [];
    this.tempId = uniqueId();
    data && this.populateData(data);
  }

  /**
   * Populate data
   * @param  {Object} access detail data
   */
  @action
  populateData(data = {}) {
    this.id = data.id;
    this.accessors = data.accessors || [];
    this.accessRight = data.accessRight;
    this.assignedType = data.assignedType;
    this.accessRightId = data.accessRightId;
    this.reportAccessRightId = data.reportAccessRightId;
    this.dashboardAccessRightId = data.dashboardAccessRightId;
    this.state = data.state;
    this.reportId = data.reportId;
    this.tempId = data.tempId || data.id;
    this.assignedTypeName = data.assignedTypeName;
    this.accessorNames = data.accessorNames || [];
    this.reportAccessRights = data.reportAccessRights;
    this.dashboardAccessRights = data.dashboardAccessRights;
  }

  /**
   * Update selected access
   * @param  {boolean} value
   */
  @action
  updateSelected(value) {
    this.selected = value;
  }

  /**
   * Check access  dirty or not
   * @return {boolean} result of dirty
   */
  checkDirty() {
    return this.isDirty;
  }

  /**
   * Update value by key
   * @param  {string} value
   * @param  {string} key
   */
  @action
  updateValueByKey(value, key) {
    this[key] = value;
    this.updateState();
    this.markDirty();
  }

  /**
   * Update access right
   * @param  {string} value
   * @param  {string} key
   */
  @action
  updateAccessRight(value, key) {
    this[key] = value.value;
    this.updateState();
    this.markDirty();
  }

  /**
   * delete action
   */
  @action
  delete() {
    this.selected = false;
    this.state = DELETE;
    this.markDirty();
  }

  /**
   * Update assigned type
   * @param  {string} value
   */
  @action
  updateAssignedType(value) {
    this.assignedType = value;
    this.accessors = [];
    this.accessorNames = [];
    this.updateState();
    this.markDirty();
  }

  /**
   * Add more accessors
   * @param  {string} value
   */
  @action
  addMoreAccessors(value) {
    forEach(item => {
      if (!isEmpty(item) && !includes(item.id, this.accessors)) {
        this.accessors.push(item.id);
        this.accessorNames.push(item.name || item.fullName);
      }
    }, value);
    this.updateState();
    this.markDirty();
  }

  /**
   * Build access data for save in tenant/ role settings
   * @return {object}
   */
  buildDataForSaveAccessSettings() {
    return {
      state: this.state,
      assignedType: this.assignedType,
      accessors: this.accessors,
      tempId: this.tempId,
      id: this.id,
      reportAccessRightId: this.reportAccessRightId,
      dashboardAccessRightId: this.dashboardAccessRightId
    };
  }

  /**
   * Update state of access detail if current state # Insert
   */
  @action
  updateState() {
    if (this.state !== INSERT) {
      this.state = UPDATE;
    }
  }

  /**
   * Update accessor list
   * @param  {Array} accessors
   */
  @action
  updateAccessorList(accessors) {
    this.accessors.splice(0, this.accessors.length);
    this.accessorNames.splice(0, this.accessorNames.length);
    this.localRoles.splice(0, this.localRoles.length);

    forEach(item => {
      this.accessors.push(item.id);
      this.accessorNames.push(item.name || item.fullName);
      isNil(item.tenantId) && this.localRoles.push(item.id);
    }, accessors);
    this.updateState();
    this.markDirty();
  }

  /**
   * Remove all global roles
   */
  @action
  removeAllGlobalRoles() {
    this.accessors = this.localRoles;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.AccessDetail.js